import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Badge utility functions for scoring
export const getBadgeVariant = (score: number) => {
  if (score >= 80) return "success"
  if (score >= 60) return "warning"
  return "destructive"
}

export const getBadgeText = (item?: { score: number } | null) => {
  const score = item?.score ?? 0
  if (score >= 80) return "Good"
  if (score >= 60) return "Average"
  return "Poor"
}

const UPPERCASE_WORDS = new Set(["ai", "ppc", "seo", "crm", "api", "url", "csv", "pdf", "roi", "usp", "gbp"])

export const humanizeString = (string: string) => {
  return string.replace(/_/g, " ").split(" ").map((word) => {
    if (UPPERCASE_WORDS.has(word.toLowerCase())) return word.toUpperCase()
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }).join(" ")
}

export const formatCurrency = (
  value: number,
  currency = "USD",
  opts?: { maximumFractionDigits?: number }
) => {
  // CPL/CPC surfaces keep sub-unit precision (e.g. PPC CPC at 2 dp); everything
  // else rounds to whole units. minimumFractionDigits tracks the max so a
  // 2-dp price still renders "$12.50" rather than "$12.5".
  const maximumFractionDigits = opts?.maximumFractionDigits ?? 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: maximumFractionDigits,
    maximumFractionDigits
  }).format(value)
}

export const currencySymbol = (currency = "USD") => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value ?? currency
}

export const formatNumber = (
  value: number,
  options?: { maximumFractionDigits?: number }
) => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: options?.maximumFractionDigits ?? 0
  }).format(value)
}

/** ActionCable WebSocket URL. SSR-safe; uses window.ACTION_CABLE_URL when set (non-empty), else "/cable". */
export function getActionCableUrl(): string {
  if (typeof window === "undefined") return "/cable"
  const url = (window as Window & { ACTION_CABLE_URL?: string }).ACTION_CABLE_URL
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string
  return (url?.trim() && url) || "/cable"
}

export function getCsrfToken(): string {
  if (typeof document === "undefined") return ""
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

/**
 * Rails-friendly CSRF POST helper for flows that must be a full-page form POST
 * (e.g. Devise/OmniAuth, Stripe connect start).
 */
export function postWithCsrf(action: string, params: Record<string, string> = {}): void {
  if (typeof document === "undefined") return

  const form = document.createElement("form")
  form.method = "POST"
  form.action = action

  const csrfInput = document.createElement("input")
  csrfInput.type = "hidden"
  csrfInput.name = "authenticity_token"
  csrfInput.value = getCsrfToken()

  form.appendChild(csrfInput)

  for (const [name, value] of Object.entries(params)) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  document.body.appendChild(form)
  form.submit()
}

// "4 days ago" / "just now" for a timestamp (Intl only — the repo carries no
// date library). `now` is injectable for tests.
const TIME_AGO_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 30],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
]

export const timeAgo = (iso: string, now: number = Date.now()) => {
  let delta = (Date.parse(iso) - now) / 1000
  if (Math.abs(delta) < 45) return "just now"
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" })
  for (const [unit, size] of TIME_AGO_STEPS) {
    if (Math.abs(delta) < size) return formatter.format(Math.round(delta), unit)
    delta /= size
  }
  return formatter.format(Math.round(delta), "year")
}
